import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, supabaseUrl } from "@/integrations/supabase/client";

export type SpeakingStatus =
  | "idle"
  | "recording"
  | "processing"
  | "success"
  | "error"
  | "quota"
  | "mic_permission";

export interface SpeakingQuotaInfo {
  tier: "free" | "premium" | "pro";
  used: number;
  limit: number;
}

interface UseSpeakingPracticeArgs {
  language: string;
  sessionId: string;
}

interface UseSpeakingPracticeApi {
  status: SpeakingStatus;
  score: number | null;
  transcription: string | null;
  error: string | null;
  quotaInfo: SpeakingQuotaInfo | null;
  recordingSeconds: number;
  startRecording: (targetText: string) => Promise<void>;
  stopRecording: () => void;
  reset: () => void;
}

const MAX_RECORDING_MS = 15_000;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("FileReader result is not a string"));
        return;
      }
      resolve(result); // keep the data-URL prefix so edge function can infer MIME
    };
    reader.readAsDataURL(blob);
  });
}

function pickSupportedMime(): string {
  // iOS Safari prefers mp4/aac; Android Chrome prefers webm/opus. Firefox prefers ogg.
  const candidates = ["audio/mp4", "audio/aac", "audio/webm;codecs=opus", "audio/webm", "audio/ogg"];
  for (const m of candidates) {
    // @ts-ignore — MediaRecorder may be absent on older browsers
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return "";
}

export function useSpeakingPractice({ language, sessionId }: UseSpeakingPracticeArgs): UseSpeakingPracticeApi {
  const [status, setStatus] = useState<SpeakingStatus>("idle");
  const [score, setScore] = useState<number | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<SpeakingQuotaInfo | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const targetTextRef = useRef<string>("");
  const mimeRef = useRef<string>("");
  const isSubmittingRef = useRef<boolean>(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current !== null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const teardownStream = useCallback(() => {
    clearTimers();
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      // best-effort cleanup
    }
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      teardownStream();
    };
  }, [teardownStream]);

  const reset = useCallback(() => {
    teardownStream();
    setStatus("idle");
    setScore(null);
    setTranscription(null);
    setError(null);
    setQuotaInfo(null);
    setRecordingSeconds(0);
  }, [teardownStream]);

  const submitAudio = useCallback(
    async (targetText: string, base64Audio: string, mime: string) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setStatus("processing");
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          setError("unauthenticated");
          setStatus("error");
          return;
        }

        const url = `${supabaseUrl}/functions/v1/language-stt-score`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            audio_base64: base64Audio,
            target_text: targetText,
            language,
            session_id: sessionId,
            mime,
          }),
        });

        if (res.status === 429) {
          const body = await res.json().catch(() => ({}));
          setQuotaInfo({
            tier: (body?.tier as SpeakingQuotaInfo["tier"]) || "free",
            used: typeof body?.used === "number" ? body.used : 0,
            limit: typeof body?.limit === "number" ? body.limit : 1,
          });
          setStatus("quota");
          return;
        }

        // V4.3: pronunciation scoring is Premium. Free now gets 402
        // module_locked from language-stt-score → reuse the in-place "quota"
        // upgrade screen instead of a generic error.
        if (res.status === 402) {
          setQuotaInfo({ tier: "free", used: 0, limit: 0 });
          setStatus("quota");
          return;
        }

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          console.error("[useSpeakingPractice] submit non-ok:", res.status, body);
          setError((body as any)?.error || "upstream_error");
          setStatus("error");
          return;
        }

        const body = await res.json();
        setScore(typeof body?.score === "number" ? body.score : 0);
        setTranscription(typeof body?.transcription === "string" ? body.transcription : "");
        setQuotaInfo({
          tier: (body?.tier as SpeakingQuotaInfo["tier"]) || "free",
          used: typeof body?.used === "number" ? body.used : 0,
          limit: typeof body?.limit === "number" ? body.limit : 1,
        });
        setStatus("success");
      } catch (err: any) {
        console.error("[useSpeakingPractice] submit threw:", err);
        setError(err?.message || "network_error");
        setStatus("error");
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [language, sessionId],
  );

  const finalizeRecording = useCallback(async () => {
    const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/mp4" });
    chunksRef.current = [];
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      // ignore
    }
    streamRef.current = null;
    mediaRecorderRef.current = null;
    clearTimers();

    if (!blob.size) {
      setError("empty_recording");
      setStatus("error");
      return;
    }

    try {
      const base64 = await blobToBase64(blob);
      await submitAudio(targetTextRef.current, base64, mimeRef.current || blob.type);
    } catch (err: any) {
      console.error("[useSpeakingPractice] finalize threw:", err);
      setError(err?.message || "encode_error");
      setStatus("error");
    }
  }, [clearTimers, submitAudio]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch (err) {
        console.error("[useSpeakingPractice] MediaRecorder.stop threw:", err);
      }
    }
  }, []);

  const startRecording = useCallback(
    async (targetText: string) => {
      setError(null);
      setScore(null);
      setTranscription(null);
      setRecordingSeconds(0);
      targetTextRef.current = targetText;
      try {
        if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
          setError("mic_unsupported");
          setStatus("mic_permission");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mime = pickSupportedMime();
        mimeRef.current = mime;
        const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e: BlobEvent) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          void finalizeRecording();
        };
        recorder.onerror = (e: any) => {
          console.error("[useSpeakingPractice] MediaRecorder error:", e?.error || e);
          setError("recorder_error");
          setStatus("error");
          teardownStream();
        };

        recorder.start();
        setStatus("recording");
        setRecordingSeconds(0);

        timeoutRef.current = window.setTimeout(() => {
          stopRecording();
        }, MAX_RECORDING_MS);

        countdownRef.current = window.setInterval(() => {
          setRecordingSeconds((s) => s + 1);
        }, 1000);
      } catch (err: any) {
        console.error("[useSpeakingPractice] getUserMedia threw:", err);
        if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
          setError("mic_permission");
        } else {
          setError(err?.message || "mic_error");
        }
        setStatus("mic_permission");
        teardownStream();
      }
    },
    [finalizeRecording, stopRecording, teardownStream],
  );

  return {
    status,
    score,
    transcription,
    error,
    quotaInfo,
    recordingSeconds,
    startRecording,
    stopRecording,
    reset,
  };
}
